import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { format } from 'date-fns-tz'

import * as styles from './ActivitySearchPanel.module.scss'

import { TIMEZONE } from '../../../constants'
import { usePropertyProvider, usePublication } from '../../../hooks'
import i18n from '../../../i18n'
import { formatJstDate } from '../../../utils'
import { Button, Flex, FlexBox, Text } from '../../basics'
import { CalendarModal, NumberOfGuestsModal } from '../../modals'

type NumbersOfGuests = { adults: number; children: number; infants: number }

type ActivitySearchPanelProps = {
  onSearch: (search: {
    checkInDate?: string
    numberOfGuests?: NumbersOfGuests
  }) => void
}

const translateNamespace = 'components.PropertySearchPanel'

export const ActivitySearchPanel: React.FC<ActivitySearchPanelProps> = ({
  onSearch,
}) => {
  const { t } = useTranslation()
  const { publications } = usePropertyProvider()
  const publication = usePublication(i18n.language, publications)
  const [calendarModalOpen, setCalendarModalOpen] = useState(false)
  const [numberOfGuestsModalOpen, setNumberOfGuestsModalOpen] = useState(false)
  const [checkInDate, setCheckInDate] = useState<Date>()
  const [numberOfGuests, setNumberOfGuests] = useState<NumbersOfGuests>()

  const displayNumberOfGuests = numberOfGuests
    ? t(`${translateNamespace}.guests`, {
        numberOfAdults: numberOfGuests.adults,
        numberOfChildren: numberOfGuests.children,
        numberOfInfants: numberOfGuests.infants,
      })
    : t(`${translateNamespace}.selectNumberOfGuests`)

  const checkInParam = checkInDate
    ? format(checkInDate, 'yyyy-MM-dd', { timeZone: TIMEZONE })
    : ''

  return (
    <React.Fragment>
      <Flex optionClass={styles.searchPanel}>
        <Flex optionClass={styles.searchPanelLeft}>
          <FlexBox optionClass={styles.searchPanelItem}>
            <Text weight="bold" pcSize={18} spSize={12}>
              {t(`${translateNamespace}.schedule`)}
            </Text>
            <button
              type="button"
              className={clsx(styles.select, !checkInDate && styles.gray)}
              onClick={() => setCalendarModalOpen(true)}
            >
              {checkInDate
                ? formatJstDate(
                    checkInParam,
                    i18n.language === 'ja' ? 'yyyy/MM/dd (E)' : 'MM/dd/yyyy'
                  )
                : t(`${translateNamespace}.selectUsageDate`)}
            </button>
          </FlexBox>

          <FlexBox optionClass={styles.divider}>divider</FlexBox>
          <FlexBox optionClass={styles.searchPanelItem}>
            <Text weight="bold" pcSize={18} spSize={12}>
              {t(`${translateNamespace}.numberOfGuests`)}
            </Text>
            <button
              type="button"
              className={clsx(styles.select, !numberOfGuests && styles.gray)}
              onClick={() => setNumberOfGuestsModalOpen(true)}
            >
              {displayNumberOfGuests}
            </button>
          </FlexBox>
          <FlexBox optionClass={clsx(styles.divider, 'sp')}>divider</FlexBox>
        </Flex>

        <FlexBox optionClass={styles.searchBtn}>
          <Button
            bold
            htmlType="button"
            variant="primary"
            onClick={() =>
              onSearch({
                checkInDate: checkInParam,
                numberOfGuests,
              })
            }
          >
            <span className={styles.searchIcon} />
            {t(`${translateNamespace}.searchAvailability`)}
          </Button>
        </FlexBox>
      </Flex>

      {calendarModalOpen && (
        <CalendarModal
          propertyName={publication?.name ?? ''}
          checkInParam={checkInParam}
          checkOutParam={checkInParam}
          isOpen={calendarModalOpen}
          onClose={() => setCalendarModalOpen(false)}
          onSearch={(checkIn) => {
            setCheckInDate(new Date(checkIn))
            setCalendarModalOpen(false)
          }}
          mode="single"
        />
      )}

      {numberOfGuestsModalOpen && (
        <NumberOfGuestsModal
          adultParam={numberOfGuests?.adults}
          childParam={numberOfGuests?.children}
          infantParam={numberOfGuests?.infants}
          isOpen={numberOfGuestsModalOpen}
          onClose={() => setNumberOfGuestsModalOpen(false)}
          onSearch={(adults, children, infants) => {
            setNumberOfGuests({ adults, children, infants })
            setNumberOfGuestsModalOpen(false)
          }}
        />
      )}
    </React.Fragment>
  )
}
